// src/hooks/useBenchmarkSuggestion.ts
//
// Encapsulates the debounced fetch + state management for the benchmark
// auto-suggestion, so the Strategy Tear Sheet (and any future benchmark picker)
// stays declarative. Re-fetches whenever the risk band or realised net
// volatility changes, debounced by 300ms to avoid hammering the API when a
// slider is dragged. Fails silently (error surfaced, badge hidden).

import { useEffect, useState } from 'react';

import {
    fetchBenchmarkSuggestion,
    type BenchmarkSuggestionResponse,
} from '../features/portfolio-optimization-tools/multi-asset-portfolio-optimizer/api';

const DEBOUNCE_MS = 300;

export interface UseBenchmarkSuggestionResult {
    suggestion: BenchmarkSuggestionResponse | null;
    isLoading: boolean;
    error: string | null;
}

export const useBenchmarkSuggestion = (
    riskProfile: string | null | undefined,
    netVolatility: number | null | undefined,
): UseBenchmarkSuggestionResult => {
    const [suggestion, setSuggestion] =
        useState<BenchmarkSuggestionResponse | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        // Nothing to suggest against until both inputs are present.
        if (
            !riskProfile
            || netVolatility === null
            || netVolatility === undefined
            || Number.isNaN(netVolatility)
        ) {
            setSuggestion(null);
            setError(null);
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        setIsLoading(true);
        setError(null);

        const timer = window.setTimeout(() => {
            fetchBenchmarkSuggestion(riskProfile, netVolatility)
                .then((result) => {
                    if (cancelled) return;
                    setSuggestion(result);
                })
                .catch((err) => {
                    if (cancelled) return;
                    // eslint-disable-next-line no-console
                    console.error('Benchmark suggestion failed', err);
                    setSuggestion(null);
                    setError('Unable to fetch a benchmark suggestion.');
                })
                .finally(() => {
                    if (!cancelled) setIsLoading(false);
                });
        }, DEBOUNCE_MS);

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [riskProfile, netVolatility]);

    return { suggestion, isLoading, error };
};

export default useBenchmarkSuggestion;
